import { Link } from "@tanstack/react-router";

import {
  DiscordIcon,
  FacebookIcon,
  InstagramIcon,
  MortyLogoIcon,
  OpenNewTabIcon,
  TikTokIcon,
  XIcon,
} from "./icons";
import MobileTabBar from "./mobile-tab-bar";

const LINKS = [
  [
    {
      isExternal: false,
      isInternal: false,
      label: "About",
      url: "/s/about",
    },
    {
      isExternal: true,
      isInternal: false,
      label: "Blog",
      url: "/blog",
    },
    {
      isExternal: true,
      isInternal: false,
      label: "Careers",
      url: "https://mortyapp.notion.site/Morty-Careers-d3918cf84cd047b495e528b61137acf9",
    },
    {
      isExternal: false,
      isInternal: false,
      label: "Contact",
      url: "/s/contact",
    },
  ],
  [
    {
      isExternal: true,
      isInternal: false,
      label: "Help",
      url: "https://help.morty.app/",
    },
    {
      isExternal: true,
      isInternal: false,
      label: "Morty for Business",
      url: "/s/morty-for-business",
    },
    {
      isExternal: true,
      isInternal: false,
      label: "Join a meetup",
      url: "/s/morty-meetup-groups",
    },
    {
      isExternal: true,
      isInternal: false,
      label: "Public roadmap",
      url: "https://roadmap.morty.app/",
    },
  ],
  [
    {
      isExternal: false,
      isInternal: false,
      label: "Open dataset",
      url: "/s/dataset",
    },
    {
      isExternal: true,
      isInternal: false,
      label: "Privacy",
      url: "https://link.morty.app/privacy-notice",
    },
    {
      isExternal: true,
      isInternal: false,
      label: "Terms of use",
      url: "https://link.morty.app/terms-of-use",
    },
    {
      isExternal: false,
      isInternal: false,
      label: "+1 (833) 987-1999",
      url: "tel:+18339871999",
    },
  ],
];

interface SocialLinkProps {
  Icon: React.ComponentType<React.ComponentProps<"svg">>;
  label: string;
  url: string;
}

const SOCIALS: SocialLinkProps[] = [
  { Icon: XIcon, label: "X", url: "https://x.com/mortyapp" },
  {
    Icon: InstagramIcon,
    label: "Instagram",
    url: "https://www.instagram.com/mortyapp/",
  },
  {
    Icon: TikTokIcon,
    label: "TikTok",
    url: "https://www.tiktok.com/@mortyapp",
  },
  {
    Icon: FacebookIcon,
    label: "Facebook",
    url: "https://www.facebook.com/mortyapp/",
  },
  { Icon: DiscordIcon, label: "Discord", url: "https://discord.gg/ymdAAh2X6d" },
];

function SocialLink({ Icon, label, url }: SocialLinkProps) {
  return (
    <a aria-label={label} className="group" href={url}>
      <Icon className="transition-opacity group-hover:opacity-80" />
    </a>
  );
}

const FOOTER_LINK_CLASSNAME =
  "text-white md:text-pale-lavender text-14/[1.71em] transition-colors hover:text-white hover:underline";

export default function Footer() {
  return (
    <>
      <footer className="relative z-1 standalone:hidden bg-[url(/images/main-bg.jpg)] bg-cover bg-top pt-10 text-center md:text-left">
        <div className="container">
          <div className="flex flex-col gap-10 md:flex-row">
            <div className="md:w-[47%]">
              <Link to="/">
                <MortyLogoIcon className="mx-auto md:mx-0" />
              </Link>
              <p className="mt-[30px] text-14/[1.71em]">
                Every escape room. Every haunt. Ever.
              </p>
              <ul className="mt-[30px] flex justify-center gap-[15px] md:justify-start">
                {SOCIALS.map((social) => (
                  <li key={social.url}>
                    <SocialLink {...social} />
                  </li>
                ))}
              </ul>
            </div>
            {LINKS.map((column, index) => (
              <ul
                className="flex flex-col items-center gap-3.5 md:grow md:items-start"
                // biome-ignore lint/suspicious/noArrayIndexKey: no choice
                key={index}
              >
                {column.map((link) => (
                  <li key={link.label}>
                    {link.isInternal ? (
                      <Link className={FOOTER_LINK_CLASSNAME} to={link.url}>
                        {link.label}
                      </Link>
                    ) : (
                      <a
                        className={FOOTER_LINK_CLASSNAME}
                        href={link.url}
                        rel={
                          link.isExternal ? "noopener noreferrer" : undefined
                        }
                        target={link.isExternal ? "_blank" : undefined}
                      >
                        {link.label}
                        {link.isExternal && <OpenNewTabIcon />}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            ))}
          </div>
          <div className="mt-10 border-white/20 border-t py-[25px] md:mt-35">
            <p className="text-center text-14/[1.71em] text-pale-lavender">
              © Morty Technologies Inc.
            </p>
          </div>
        </div>
      </footer>
      <MobileTabBar />
      <div className="standalone:block hidden h-[150px]" />
    </>
  );
}
